import { FiMoreVertical } from 'react-icons/fi';
import Image from 'next/image';
import { RoleList, ViewCommentMode } from '../../../constant/comment';
import { ROLE_ACCESS, TRoleAccess } from '../../../constant/storage';
import {
  ICommentSideModal,
  ICommentUserInfo,
} from '../../../interfaces/components/comment.interface';
import { Dispatch, SetStateAction, useContext, useState } from 'react';
import {
  PinIcon,
  PinIconHover,
  EditComment,
  EditCommentHover,
  TrashNoColorIcon,
  TrashIconHover,
} from '../../../constant/imgImport';
import { Drawer, Popover, useMediaQuery } from '@mui/material';
import AuthContext from '../../../state/auth/AuthContext';
import { ICommentInteract } from '../../../interfaces/hook.interface';

interface ICommentCardMenu extends ICommentInteract {
  userInfo: ICommentUserInfo;
  isPinned?: boolean;
  setModeTo: Dispatch<SetStateAction<ViewCommentMode>>;
  parentId?: string;
  id: string;
}

const CommentCardMenu = ({
  userInfo,
  openConfirm,
  toast,
  handleDeleteGlobal,
  handleTwoWaysPinGlobal,
  setModeTo,
  isPinned,
  parentId,
  id,
}: ICommentCardMenu & ICommentSideModal) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { setLoading } = useContext(AuthContext);

  const matches = useMediaQuery('(min-width:768px)');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  const handleDelete = async () => {
    try {
      await handleDeleteGlobal(id);
      toast(`Xoá ${parentId ? 'phản hồi' : 'bình luận'} thành công`, 'success');
    } catch (error) {
      toast(
        `Có lỗi xảy ra khi xoá ${parentId ? 'phản hồi' : 'bình luận'}`,
        'error'
      );
      // console.log(error);
    }
  };

  const handlePinComment = async () => {
    setLoading(true);
    try {
      if (handleTwoWaysPinGlobal) {
        await handleTwoWaysPinGlobal(id);
        toast(
          `Đã ${isPinned ? 'bỏ ghim' : 'ghim'} ${
            parentId ? 'phản hồi' : 'bình luận'
          }!`,
          'success'
        );
      }
    } catch (error) {
      toast(
        `Thất bại khi ghim ${
          parentId ? 'phản hồi' : 'bình luận'
        }. Kiểm tra kết nối của bạn và thử lại nhé!`,
        'error'
      );
    }
    setLoading(false);
  };

  const popoverModal = (
    <PopoverModal
      commentRole={userInfo.commentRole}
      commentSectionRole={userInfo.commentSectionRole}
      onClickHandlers={{
        handleEdit: () => setModeTo(ViewCommentMode.Edit),
        handleDelete: () =>
          openConfirm({
            confirmCallback: handleDelete,
            title: `Xoá ${parentId ? 'phản hồi' : 'bình luận'}?`,
            description: `Bạn có muốn xoá vĩnh viễn ${
              parentId ? 'phản hồi' : 'bình luận'
            } này không?`,
            onClose: () => {},
            cancelCallback: () => {},
            isOpen: true,
          }),
        handlePin: handlePinComment,
      }}
      handleClose={handleClose}
      parentId={parentId}
      isPinned={isPinned}
    />
  );

  return (
    <>
      <div className="w-[36px] flex justify-center">
        {(userInfo.commentRole === RoleList.Author ||
          userInfo.commentSectionRole === ROLE_ACCESS.Owner) && (
          <button onClick={handleClick}>
            <FiMoreVertical role={'button'} className="w-6 h-6 text-gray-500" />
          </button>
        )}
      </div>
      {matches ? (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          className="mt-4"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          {popoverModal}
        </Popover>
      ) : (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          PaperProps={{
            square: false,
            style: {
              borderTopLeftRadius: '10px',
              borderTopRightRadius: '10px',
              borderBottomLeftRadius: '0px',
              borderBottomRightRadius: '0px',
            },
          }}
        >
          <h1 className="pt-4 px-3 font-bold text-xl leading-[25px] text-[#242424]">
            Mở rộng
          </h1>
          {popoverModal}
        </Drawer>
      )}
    </>
  );
};

const PopoverModal = ({
  commentRole,
  commentSectionRole,
  onClickHandlers,
  handleClose,
  parentId,
  isPinned,
}: {
  commentRole: RoleList;
  commentSectionRole: TRoleAccess;
  onClickHandlers: {
    handlePin: Function;
    handleEdit: Function;
    handleDelete: Function;
  };
  handleClose: () => void;
  parentId?: string;
  isPinned?: boolean;
}) => {
  const actionList = [
    commentSectionRole === ROLE_ACCESS.Owner && !parentId
      ? {
          name: `${isPinned ? 'Bỏ ghim' : 'Ghim'} bình luận`,
          icon: PinIcon,
          hoverIcon: PinIconHover,
          onClick: () => {
            onClickHandlers.handlePin();
            handleClose();
          },
        }
      : undefined,
    commentRole === RoleList.Author
      ? {
          name: `Sửa ${!parentId ? 'bình luận' : 'phản hồi'}`,
          icon: EditComment,
          hoverIcon: EditCommentHover,
          onClick: () => {
            onClickHandlers.handleEdit();
            handleClose();
          },
        }
      : undefined,
    {
      name: `Xoá ${!parentId ? 'bình luận' : 'phản hồi'}`,
      icon: TrashNoColorIcon,
      hoverIcon: TrashIconHover,
      onClick: () => {
        onClickHandlers.handleDelete();
        handleClose();
      },
    },
  ];

  return (
    <div className="md:w-[285px] w-full py-2">
      {actionList
        .filter((action) => action !== undefined)
        .map((action: any, _key: number) => {
          return <EachActionItem key={_key + action.name} action={action} />;
        })}
    </div>
  );
};

const EachActionItem = ({
  action,
}: {
  action: {
    name: string;
    onClick: () => void;
    icon: string;
    hoverIcon: string;
  };
}) => {
  const [icon, setIcon] = useState<string>(action.icon);
  return (
    <div
      onMouseEnter={() => setIcon(action.hoverIcon)}
      onMouseLeave={() => setIcon(action.icon)}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        action.onClick();
      }}
      className="flex flex-row-reverse md:flex-row w-full px-[35px] md:px-4 md:py-2 py-4 cursor-pointer gap-[15px] justify-end md:gap-0 md:justify-between items-center hover:text-primary hover:bg-blue-100 transition-all ease-linear"
    >
      <p> {action.name} </p>
      <Image src={icon} alt="menu_icon" height={20} width={20} />
    </div>
  );
};

export default CommentCardMenu;
